import { ActionReducerMapBuilder, AsyncThunk, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ethers } from 'ethers';
import { formatUnits, parseUnits } from 'ethers';
import SmartContractService from '../services/smartContractService';
import { 
  DepositPayload, 
  WithdrawPayload, 
  AddPointsPayload,
  RefreshPointsPayload
} from '../models/DepositsContract';
import type { WritableDraft } from 'immer';

// Constants
const TOKEN_DECIMALS = 18;
const POINTS_DECIMALS = 0;


interface UserBalance {
  fiat: string;
  token: string;
}

interface ContractState {
  contractAddress: string | null;
  signerAddress: string | null;
  admin: string | null;
  pointsManager: string | null;
  userBalance: UserBalance;
  userPoints: string;
  loading: boolean;
  error: string | null;
  transactionStatus: 'idle' | 'pending' | 'success' | 'failed';
  isMobile: boolean;
  isMetaMaskConnected: boolean;
}
type TransactionStatus = ContractState['transactionStatus'];

const initialState: ContractState = {
  contractAddress: null,
  signerAddress: null,
  admin: null,
  pointsManager: null,
  userBalance: {
    fiat: '0',
    token: '0'
  },
  userPoints: '0',
  loading: false,
  error: null,
  transactionStatus: 'idle',
  isMobile: false,
  isMetaMaskConnected: false
};

const handleContractError = (error: unknown, dispatch: any) => {
  console.error('Contract error:', error);
  const errorMessage = error instanceof Error 
    ? error.message 
    : typeof error === 'string' 
      ? error 
      : 'Unknown error occurred';
  dispatch(setError(errorMessage));
  throw error;
};

// Thunks
export const checkConnection = createAsyncThunk(
  'deposits/checkConnection',
  async (_, { dispatch }) => {
    try {
      const { isConnected, isMobile } = await SmartContractService.checkConnection();
      dispatch(setIsMobile(isMobile));
      dispatch(setIsMetaMaskConnected(isConnected));
    } catch (error) {
      dispatch(setError(error instanceof Error ? error.message : 'Connection check failed'));
    }
  }
);

export const connectWallet = createAsyncThunk(
  'deposits/connectWallet',
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      await SmartContractService.connectWallet();
      await dispatch(initializeContract());
      dispatch(setIsMetaMaskConnected(true));
    } catch (error) {
      dispatch(setError(error instanceof Error ? error.message : 'Wallet connection failed'));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const initializeContract = createAsyncThunk(
  'deposits/initializeContract',
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      await SmartContractService.initializeContracts();
      
      const contract = SmartContractService.getDepositsContract();
      const signerAddress = await SmartContractService.getSignerAddress();
      
      const [balanceStruct, userPoints, admin, pointsManager] = await Promise.all([
        contract.balances(signerAddress),
        contract.points(signerAddress),
        contract.admin(),
        contract.pointsManager()
      ]);

      dispatch(setContractInfo({
        address: contract.target.toString(),
        signerAddress
      }));
      
      dispatch(setUserBalance({
        fiat: formatUnits(balanceStruct.fiat, 18),
        token: formatUnits(balanceStruct.token, 18)
      }));
      
      dispatch(setUserPoints(formatUnits(userPoints, 0)));
      dispatch(setRoles({ admin, pointsManager }));
    } catch (error) {
      dispatch(setError(error instanceof Error ? error.message : 'Contract initialization failed'));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const depositFunds = createAsyncThunk(
  'deposits/depositFunds',
  async ({ amount, isToken, tokenAddress }: DepositPayload, { getState, dispatch }) => {
    

    const contract = SmartContractService.getDepositsContract();
    if (!contract) {
      throw new Error('Contract not initialized');
    }

    try {
      const method = isToken ? 'depositToken' : 'depositFiat';
      const args = isToken 
        ? [parseUnits(amount, TOKEN_DECIMALS)] 
        : [tokenAddress, parseUnits(amount, TOKEN_DECIMALS)];

      const tx = await contract[method](...args);
      await tx.wait();
      
      dispatch(refreshBalances());
    } catch (error) {
      handleContractError(error, dispatch);
    }
  }
);

export const withdrawFunds = createAsyncThunk(
  'deposits/withdrawFunds',
  async ({ amount, isToken, tokenAddress }: WithdrawPayload, { getState, dispatch }) => {
    
    const contract = SmartContractService.getDepositsContract();
    if (!contract) {
      throw new Error('Contract not initialized');
    }

    try {
      const method = isToken ? 'withdrawToken' : 'withdrawFiat';
      const args = isToken 
        ? [parseUnits(amount, TOKEN_DECIMALS)] 
        : [tokenAddress, parseUnits(amount, TOKEN_DECIMALS)];

        

      const tx = await contract[method](...args);
      await tx.wait();
      
      dispatch(refreshBalances());
    } catch (error) {
      handleContractError(error, dispatch);
    }
  }
);

export const addPoints = createAsyncThunk(
  'deposits/addPoints',
  async ({ userAddress, points, privateKey }: AddPointsPayload, { getState, dispatch }) => {

    
    const depositsContract = SmartContractService.getDepositsContract();

    try {
      const tx = await depositsContract.addPoints(
        userAddress,
        parseUnits(points, POINTS_DECIMALS)
      );
      await tx.wait();
      
      dispatch(refreshPoints({ userAddress, privateKey }));
    } catch (error) {
      handleContractError(error, dispatch);
    }
  }
);

export const redeemPoints = createAsyncThunk(
  'deposits/redeemPoints',
  async (_, { getState, dispatch }) => {
    
    const depositsContract = SmartContractService.getDepositsContract();
    try {
      const tx = await depositsContract.redeemPoints();
      await tx.wait();
      
      dispatch(refreshBalances());
    } catch (error) {
      handleContractError(error, dispatch);
    }
  }
);

export const refreshBalances = createAsyncThunk(
  'deposits/refreshBalances',
  async (_, { getState, dispatch }) => {
    console.log('Refreshing balances...');
    const depositsContract = SmartContractService.getDepositsContract();

    console.log('Deposits contract:', depositsContract);

    if (!depositsContract) return;

    try {
      const runner = depositsContract.runner;
      if (!runner || typeof (runner as ethers.Signer).getAddress !== 'function') {
        throw new Error('Signer not available on contract');
      }
      const userAddress = await (runner as ethers.Signer).getAddress();
      const balanceStruct = await depositsContract.balances(userAddress);

      console.log('Balance struct:', balanceStruct);
      
      dispatch(setUserBalance({
        fiat: formatUnits(balanceStruct.fiat, TOKEN_DECIMALS),
        token: formatUnits(balanceStruct.token, TOKEN_DECIMALS)
      }));
    } catch (error) {
      handleContractError(error, dispatch);
    }
  }
);

export const refreshPoints = createAsyncThunk(
  'deposits/refreshPoints',
  async (
    { userAddress, privateKey }: RefreshPointsPayload = { userAddress: '', privateKey: '' },
    { getState, dispatch }
  ) => {
    
    const depositsContract = SmartContractService.getDepositsContract();
    try {
            const address = userAddress || SmartContractService.getSignerAddress();
      const points = await depositsContract.points(address);
      
      dispatch(setUserPoints(formatUnits(points, POINTS_DECIMALS)));
    } catch (error) {
      handleContractError(error, dispatch);
    }
  }
);

// Slice
const depositsSlice = createSlice({
  name: 'deposits',
  initialState,
  reducers: {
    setContractInfo(state, action: PayloadAction<{
      address: string;
      signerAddress: string;
    }>) {
      state.contractAddress = action.payload.address;
      state.signerAddress = action.payload.signerAddress;
    },
    // setContract(state, action: PayloadAction<ethers.Contract>) {
    //   state.contract = action.payload;
    // },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setUserBalance(state, action: PayloadAction<UserBalance>) {
      state.userBalance = action.payload;
    },
    setUserPoints(state, action: PayloadAction<string>) {
      state.userPoints = action.payload;
    },
    setRoles(state, action: PayloadAction<{admin: string, pointsManager: string}>) {
      state.admin = action.payload.admin;
      state.pointsManager = action.payload.pointsManager;
    },
    setTransactionStatus(state, action: PayloadAction<TransactionStatus>) {
      state.transactionStatus = action.payload;
    },
    setIsMobile(state, action: PayloadAction<boolean>) {
      state.isMobile = action.payload;
    },
    setIsMetaMaskConnected(state, action: PayloadAction<boolean>) {
      state.isMetaMaskConnected = action.payload;
    },
    resetDepositState: () => initialState
  },
  extraReducers: (builder) => {
        
    const handleRejected = (state: ContractState, action: any) => {
      state.transactionStatus = 'failed';
      state.error = action.error.message || 'Operation failed';
    };

    builder
      .addCase(initializeContract.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeContract.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initializeContract.rejected, (state, action) => {
        state.loading = false;
        handleRejected(state, action);
      })
      .addCase(connectWallet.pending, (state) => {
        state.loading = true;
      })
      .addCase(connectWallet.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(connectWallet.rejected, (state, action) => {
        state.loading = false;
        handleRejected(state, action);
      });

// Add typed cases for each async thunk
  const addTypedCase = <T>(
    builder: ActionReducerMapBuilder<ContractState>,
    thunk: AsyncThunk<T, any, any>,
    handlers: {
      pending?: (state: WritableDraft<ContractState>) => void,
      fulfilled?: (state: WritableDraft<ContractState>) => void,
      rejected?: (state: WritableDraft<ContractState>, action: any) => void
    }
  ) => {
    if (handlers.pending) {
      builder.addCase(thunk.pending, handlers.pending);
    }
    if (handlers.fulfilled) {
      builder.addCase(thunk.fulfilled, handlers.fulfilled);
    }
    if (handlers.rejected) {
      builder.addCase(thunk.rejected, handlers.rejected);
    }
  };

  // Add all your transaction cases using the typed helper
  addTypedCase(builder, depositFunds, {
    pending: (state) => { state.transactionStatus = 'pending'; },
    fulfilled: (state) => { state.transactionStatus = 'success'; },
    rejected: (state, action) => {
      state.transactionStatus = 'failed';
      state.error = action.error.message || 'Deposit failed';
    }
  });

  // Repeat for other actions
  addTypedCase(builder, withdrawFunds, {
    pending: (state) => { state.transactionStatus = 'pending'; },
    fulfilled: (state) => { state.transactionStatus = 'success'; },
    rejected: (state, action) => {
      state.transactionStatus = 'failed';
      state.error = action.error.message || 'Withdrawal failed';
    }
  });

  addTypedCase(builder, addPoints, {
    pending: (state) => { state.transactionStatus = 'pending'; },
    fulfilled: (state) => { state.transactionStatus = 'success'; },
    rejected: (state, action) => {
      state.transactionStatus = 'failed';
      state.error = action.error.message || 'Add points failed';
    }
  });

  addTypedCase(builder, redeemPoints, {
    pending: (state) => { state.transactionStatus = 'pending'; },
    fulfilled: (state) => { state.transactionStatus = 'success'; },
    rejected: (state, action) => {
      state.transactionStatus = 'failed';
      state.error = action.error.message || 'Redeem points failed';
    }
  });

  }
});

export const { 
  setContractInfo,
  setLoading,
  setError,
  setUserBalance,
  setUserPoints,
  setRoles,
  setTransactionStatus,
  setIsMobile,
  setIsMetaMaskConnected,
  resetDepositState
} = depositsSlice.actions;

export default depositsSlice.reducer;